import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Package,
    Clock,
    ChevronRight,
    User,
    Heart,
    RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useShop } from '../context/ShopContext';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../hooks/useFavorites';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { buildLoginUrl } from '../utils/safeRedirect';
import FavoriteProductCard from '../components/FavoriteProductCard';

const API_URL = process.env.REACT_APP_API_URL;

const STATUS_MAP = {
    pending: { label: 'Εκκρεμεί', color: 'bg-yellow-100 text-yellow-800' },
    confirmed: { label: 'Επιβεβαιωμένη', color: 'bg-blue-100 text-blue-800' },
    processing: { label: 'Σε επεξεργασία', color: 'bg-indigo-100 text-indigo-800' },
    shipped: { label: 'Απεστάλη', color: 'bg-purple-100 text-purple-800' },
    delivered: { label: 'Παραδόθηκε', color: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Ακυρώθηκε', color: 'bg-red-100 text-red-800' },
};

const TABS = [
    { id: 'orders', label: 'Παραγγελίες', icon: Package },
    { id: 'buyAgain', label: 'Αγόρασε ξανά', icon: RotateCcw },
    { id: 'favorites', label: 'Αγαπημένα', icon: Heart },
];

export default function AccountPage() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [buyAgainProducts, setBuyAgainProducts] = useState([]);
    const [buyAgainLoading, setBuyAgainLoading] = useState(false);
    const [favoriteProducts, setFavoriteProducts] = useState([]);
    const [favoritesLoading, setFavoritesLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('orders');

    const { user, getAuthHeaders, isAuthenticated, resendVerificationEmail, syncCustomerFromApi } = useAuth();
    const [resendLoading, setResendLoading] = useState(false);
    const { shopName, merchantId } = useShop();
    const { addToCart } = useCart();
    const { favoriteIds, removeFavorite } = useFavorites(merchantId);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'favorites' || tabParam === 'buyAgain' || tabParam === 'orders') {
            setActiveTab(tabParam === 'buyAgain' ? 'buyAgain' : tabParam);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(buildLoginUrl('/account'));
            return;
        }
        syncCustomerFromApi();
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/storefront/my-orders`, {
                headers: getAuthHeaders(),
            });
            setOrders(response.data);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const buyAgainIds = useMemo(() => {
        const ids = [];
        const seen = new Set();
        for (const order of orders) {
            for (const item of order.items || []) {
                const pid = item.product_id || item.id;
                if (pid && !seen.has(pid)) {
                    seen.add(pid);
                    ids.push(pid);
                }
            }
        }
        return ids;
    }, [orders]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!buyAgainIds.length) {
                setBuyAgainProducts([]);
                return;
            }
            setBuyAgainLoading(true);
            try {
                const results = await Promise.all(
                    buyAgainIds.map(async (id) => {
                        try {
                            const { data } = await axios.get(`${API_URL}/products/${id}`);
                            return data;
                        } catch {
                            return null;
                        }
                    })
                );
                if (!cancelled) setBuyAgainProducts(results.filter(Boolean));
            } finally {
                if (!cancelled) setBuyAgainLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [buyAgainIds]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!favoriteIds.length) {
                setFavoriteProducts([]);
                return;
            }
            setFavoritesLoading(true);
            try {
                const results = await Promise.all(
                    favoriteIds.map(async (id) => {
                        try {
                            const { data } = await axios.get(`${API_URL}/products/${id}`);
                            return data;
                        } catch {
                            return null;
                        }
                    })
                );
                if (!cancelled) setFavoriteProducts(results.filter(Boolean));
            } finally {
                if (!cancelled) setFavoritesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [favoriteIds]);

    const needsEmailVerification = user && user.email_verified === false;

    const handleResendVerification = async () => {
        setResendLoading(true);
        const result = await resendVerificationEmail();
        setResendLoading(false);
        if (result.success) {
            toast.success('Στάλθηκε νέο email επιβεβαίωσης.');
        } else {
            toast.error(result.error);
        }
    };

    const formatDate = (dateStr) => {
        try {
            return new Date(dateStr).toLocaleDateString('el-GR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    const handleAddToCart = async (product) => {
        await addToCart(product, 1);
    };

    const handleRemoveFavorite = (productId) => {
        removeFavorite(productId);
        toast.success('Αφαιρέθηκε από τα αγαπημένα');
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Header />

            <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8">
                {needsEmailVerification && (
                    <div
                        className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
                        data-testid="verify-email-banner"
                    >
                        <p className="font-medium mb-1">Επιβεβαιώστε το email σας</p>
                        <p className="text-amber-900/90 mb-3">
                            Για να συνδέσουμε τις παραγγελίες που κάνατε ως επισκέπτης με τον λογαριασμό σας, ανοίξτε το
                            σύνδεσμο στο email που σας στείλαμε. Μέχρι τότε εμφανίζονται μόνο οι παραγγελίες ενώ είστε
                            συνδεδεμένοι.
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-full border-amber-300"
                            disabled={resendLoading}
                            onClick={handleResendVerification}
                        >
                            {resendLoading ? 'Αποστολή...' : 'Επανάληψη αποστολής email'}
                        </Button>
                    </div>
                )}

                <div className="flex items-center gap-4 mb-6" data-testid="account-profile">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-semibold text-primary">{user?.name}</h1>
                        <p className="text-sm text-muted-foreground">{user?.email}</p>
                    </div>
                </div>

                {/* Tab bar — mobile-friendly */}
                <div
                    className="sticky top-0 z-10 -mx-4 sm:mx-0 px-4 sm:px-0 py-2 mb-6 bg-background/95 backdrop-blur border-b sm:border-b-0 sm:bg-transparent sm:backdrop-blur-none"
                    data-testid="account-tabs"
                >
                    <div className="flex gap-1 sm:gap-2 p-1 rounded-xl bg-muted/60 sm:bg-muted/40 overflow-x-auto scrollbar-hide">
                        {TABS.map(({ id, label, icon: Icon }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setActiveTab(id)}
                                data-testid={`account-tab-${id}`}
                                className={`flex-1 sm:flex-none min-w-0 flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                                    activeTab === id
                                        ? 'bg-primary text-primary-foreground shadow-sm'
                                        : 'text-muted-foreground hover:bg-background/80'
                                }`}
                            >
                                <Icon className="w-4 h-4 shrink-0" />
                                <span className="truncate">{label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {activeTab === 'orders' && (
                    <div data-testid="account-section-orders">
                        {loading ? (
                            <div className="text-center py-12 text-muted-foreground">Φόρτωση...</div>
                        ) : orders.length === 0 ? (
                            <div className="text-center py-12 bg-muted/30 rounded-2xl" data-testid="no-orders">
                                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">Δεν έχετε παραγγελίες ακόμα</p>
                                <Link to="/">
                                    <Button className="mt-4 rounded-full">Ξεκινήστε τις αγορές σας</Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-4" data-testid="orders-list">
                                {orders.map((order) => {
                                    const status = STATUS_MAP[order.status] || STATUS_MAP.pending;
                                    return (
                                        <div
                                            key={order.id}
                                            className="bg-card border rounded-xl p-4 hover:shadow-sm transition-shadow"
                                            data-testid={`order-${order.id}`}
                                        >
                                            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <span
                                                        className={`text-xs font-medium px-2.5 py-1 rounded-full ${status.color}`}
                                                    >
                                                        {status.label}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {formatDate(order.created_at)}
                                                    </span>
                                                </div>
                                                <span className="font-semibold">
                                                    {(order.total_price || order.total || 0).toFixed(2)}€
                                                </span>
                                            </div>

                                            <div className="space-y-2">
                                                {(order.items || []).slice(0, 3).map((item, idx) => (
                                                    <div key={idx} className="flex items-center gap-3">
                                                        {item.image_url && (
                                                            <img
                                                                src={
                                                                    item.image_url.startsWith('/api')
                                                                        ? `${API_URL.replace('/api', '')}${item.image_url}`
                                                                        : item.image_url
                                                                }
                                                                alt={item.name}
                                                                className="w-10 h-10 rounded-lg object-cover"
                                                            />
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate">{item.name}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {item.quantity}x {item.price?.toFixed(2)}€
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                                {(order.items || []).length > 3 && (
                                                    <p className="text-xs text-muted-foreground">
                                                        +{order.items.length - 3} ακόμα προϊόντα
                                                    </p>
                                                )}
                                            </div>

                                            {order.tracking_number && (
                                                <div className="mt-3 pt-3 border-t flex items-center gap-2 text-sm flex-wrap">
                                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                                    <span className="text-muted-foreground">Tracking:</span>
                                                    <span className="font-medium">{order.tracking_number}</span>
                                                    {order.courier && (
                                                        <span className="text-muted-foreground">({order.courier})</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'buyAgain' && (
                    <div data-testid="account-section-buy-again">
                        {buyAgainLoading ? (
                            <div className="text-center py-12 text-muted-foreground">Φόρτωση προϊόντων...</div>
                        ) : buyAgainProducts.length === 0 ? (
                            <div className="text-center py-12 bg-muted/30 rounded-2xl">
                                <RotateCcw className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">
                                    Εδώ θα εμφανίζονται προϊόντα από παλιές αγορές σας.
                                </p>
                                <p className="text-sm text-muted-foreground mt-2">Δεν βρέθηκαν ακόμα.</p>
                                <Link to="/">
                                    <Button className="mt-4 rounded-full">Μετάβαση στο κατάστημα</Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {buyAgainProducts.map((p) => (
                                    <FavoriteProductCard key={p.id} product={p} onAddToCart={handleAddToCart} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'favorites' && (
                    <div data-testid="account-section-favorites">
                        {favoritesLoading ? (
                            <div className="text-center py-12 text-muted-foreground">Φόρτωση...</div>
                        ) : favoriteProducts.length === 0 ? (
                            <div className="text-center py-12 bg-muted/30 rounded-2xl">
                                <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">Δεν έχετε αγαπημένα ακόμα.</p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Πατήστε την καρδιά σε κάποιο προϊόν (ή «Αγαπημένα» στο μενού) για αυτό το κατάστημα.
                                </p>
                                <Link to="/">
                                    <Button className="mt-4 rounded-full">Περιήγηση προϊόντων</Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {favoriteProducts.map((p) => (
                                    <FavoriteProductCard
                                        key={p.id}
                                        product={p}
                                        onAddToCart={handleAddToCart}
                                        onRemoveFavorite={handleRemoveFavorite}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>
            <Footer shopName={shopName} />
        </div>
    );
}
